/**
 * The required daily warm-up.
 *  1. Review each earlier table (from the 9s on; never the 1s, 2s, 5s, 10s), one stage lighter each lesson:
 *     count forward and back (TYPED), draw the keypad (stages 2–3), then a few problems at that stage.
 *  2. Today's table: count forward and back on the keypad (TAPPED, with direction arrows), twice.
 *     Round 2 shows only the number just counted.
 */
import { SETTINGS, UNITS, REVIEW_FROM, reviewStageIndex, isDiv, type Unit, type Stage, parseKey } from "./curriculum";
import { revisitIn, strugglingIn, type StudentProgress } from "./progress";

export interface CountRow {
  kind: "count";
  table: number;
  dir: "forward" | "back";
  /** Numbers in order: 9…90 forward, 90…9 back. Key = target % 10. */
  targets: number[];
  round: number;
  rounds: number;
  review: boolean;
  /** Typed in boxes (review) or tapped on the keypad with arrows (today's table). */
  input: "typed" | "tapped";
  /** Round 2+: only the number just counted stays on screen. */
  fade: boolean;
  /** Review stage index (0–3) when review; decides whether a keypad is shown while counting. */
  stage: number;
}
export interface BuildRow {
  kind: "build";
  table: number;
  review: true;
  stage: number;
}
export interface FactRow {
  kind: "fact";
  a: number;
  b: number;
  review: true;
  /** Review stage index: decides keypad/puzzle/anchor cue/no tools. */
  stage: number;
  /** When set, this stage definition is used instead of PATTERN_STAGES[stage]. */
  stageDef?: Stage;
}

/** Division lessons' multiplication review: no scaffold on screen, keypad and anchor available with a click. */
export const REVIEW_WITH_TOOLS: Stage = { name: "Review", desc: "Times facts, headed for under 3 seconds. The keypad and the anchor are a click away if you need them.", tools: true };
/** Every Nth warm-up day in the division lessons is a full review across all tables instead of the struggling facts. */
export const FULL_REVIEW_EVERY = 5;
export const DIV_REVIEW_FACTS = 12;
export type WarmRow = CountRow | BuildRow | FactRow;

export const countTargets = (table: number, dir: "forward" | "back"): number[] => {
  const t = Array.from({ length: SETTINGS.COUNT_TO }, (_, i) => (i + 1) * table);
  return dir === "forward" ? t : t.reverse();
};

/** Tables reviewed before this unit's lesson. */
export function reviewTables(u: Unit): number[] {
  const from = UNITS.findIndex((x) => x.id === REVIEW_FROM);
  const idx = UNITS.indexOf(u);
  return UNITS.slice(from, Math.max(from, idx))
    .filter((x) => !x.special && !isDiv(x))
    .flatMap((x) => x.tables);
}

/** Every times table taught (the division lessons review all of them). */
export const allTables = (): number[] => UNITS.filter((x) => !x.special && !isDiv(x)).flatMap((x) => x.tables);

export function buildWarmup(u: Unit, p: StudentProgress, rng: () => number = Math.random): WarmRow[] {
  const rows: WarmRow[] = [];
  const shuffle = <T>(arr: T[]): T[] => {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [arr[i], arr[j]] = [arr[j]!, arr[i]!];
    }
    return arr;
  };
  if (isDiv(u)) {
    // Division lessons (Meliesa, Oct 29): a multiplication review first — the facts this student struggles with,
    // and every 5th day a full review across all tables — then count today's divisor forward and back.
    const tables = allTables();
    const full = (p.warmDays.length + 1) % FULL_REVIEW_EVERY === 0;
    let facts: [number, number][] = full ? [] : shuffle(strugglingIn(p, tables)).map(parseKey);
    const pool = shuffle(tables.flatMap((t) => Array.from({ length: 12 }, (_, b) => [t, b + 1] as [number, number])));
    for (const x of pool) if (facts.length < DIV_REVIEW_FACTS && !facts.some((f) => f[0] === x[0] && f[1] === x[1])) facts.push(x);
    for (const [a, b] of facts.slice(0, DIV_REVIEW_FACTS)) rows.push({ kind: "fact", a, b, review: true, stage: 3, stageDef: REVIEW_WITH_TOOLS });
    if (!u.mixed) for (const t of u.tables) {
      rows.push({ kind: "count", table: t, dir: "forward", targets: countTargets(t, "forward"), round: 0, rounds: 1, review: true, input: "typed", fade: false, stage: 3 });
      rows.push({ kind: "count", table: t, dir: "back", targets: countTargets(t, "back"), round: 0, rounds: 1, review: true, input: "typed", fade: false, stage: 3 });
    }
    return rows;
  }
  for (const t of reviewTables(u)) {
    const stage = reviewStageIndex(t, u.id);
    // Build the keypad first (Meliesa, Oct 29), so it's there for the counting that follows.
    if (stage === 1 || stage === 2) rows.push({ kind: "build", table: t, review: true, stage });
    rows.push({ kind: "count", table: t, dir: "forward", targets: countTargets(t, "forward"), round: 0, rounds: 1, review: true, input: "typed", fade: false, stage });
    rows.push({ kind: "count", table: t, dir: "back", targets: countTargets(t, "back"), round: 0, rounds: 1, review: true, input: "typed", fade: false, stage });
    const rev = shuffle(revisitIn(p, [t])).map(parseKey);
    const pool = shuffle(Array.from({ length: 12 }, (_, b) => [t, b + 1] as [number, number])).filter((x) => !rev.some((r) => r[1] === x[1]));
    for (const [a, b] of rev.concat(pool).slice(0, SETTINGS.REVIEW_PER)) rows.push({ kind: "fact", a, b, review: true, stage });
  }
  if (!u.special) {
    for (const t of u.tables) {
      for (let r = 0; r < SETTINGS.WARM_ROUNDS; r++) {
        rows.push({ kind: "count", table: t, dir: "forward", targets: countTargets(t, "forward"), round: r, rounds: SETTINGS.WARM_ROUNDS, review: false, input: "tapped", fade: r > 0, stage: 0 });
        rows.push({ kind: "count", table: t, dir: "back", targets: countTargets(t, "back"), round: r, rounds: SETTINGS.WARM_ROUNDS, review: false, input: "tapped", fade: r > 0, stage: 0 });
      }
    }
  }
  return rows;
}

/** A running count: feed it key taps (or typed numbers). */
export class CountRun {
  i = 0;
  constructor(public readonly row: CountRow) {}
  get done(): boolean {
    return this.i >= this.row.targets.length;
  }
  get target(): number | undefined {
    return this.row.targets[this.i];
  }
  get previous(): number | null {
    if (this.i > 0) return this.row.targets[this.i - 1]!;
    return this.row.dir === "forward" ? 0 : null;
  }
  /** Returns the number counted when the tap is right, or null when it isn't. */
  tap(digit: number): number | null {
    const t = this.target;
    if (t === undefined) return null;
    if (digit !== t % 10) return null;
    this.i++;
    return t;
  }
  /** Typed whole number. */
  enter(value: number): boolean {
    const t = this.target;
    if (t === undefined || value !== t) return false;
    this.i++;
    return true;
  }
}
